export const COD_TIPO_DECLARACAO_VINCULO = 9;

const OTHER_REASON = "outros";

const normalize = (value: string): string =>
  value
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();

export const isOtherReason = (descricao: string): boolean =>
  normalize(descricao) === OTHER_REASON;

export const COD_MOTIVO_RECEITUARIO_AZUL = 6;

export const COD_MOTIVO_RECEITUARIO_AMARELO = 9;

export const CONSELHO_DO_RECEITUARIO_ESPECIAL = "CRM";

export const isReceituarioEspecial = (codMotivoDocumento?: number): boolean =>
  codMotivoDocumento === COD_MOTIVO_RECEITUARIO_AZUL ||
  codMotivoDocumento === COD_MOTIVO_RECEITUARIO_AMARELO;

export const PRAZO_DE_ASSINATURA_PADRAO = "10 dias";

export const PRAZO_DE_ASSINATURA_RECEITUARIO_AMARELO = "48 horas";

export const UF_DO_PRAZO_RECEITUARIO_AMARELO = "RJ";

export const prazoDeAssinatura = (
  codMotivoDocumento: number,
  siglaUF?: string | null,
): string =>
  codMotivoDocumento === COD_MOTIVO_RECEITUARIO_AMARELO &&
  siglaUF === UF_DO_PRAZO_RECEITUARIO_AMARELO
    ? PRAZO_DE_ASSINATURA_RECEITUARIO_AMARELO
    : PRAZO_DE_ASSINATURA_PADRAO;

export const podeEmitirReceituarioEspecial = (
  codConselhoClasse?: string | null,
): boolean =>
  (codConselhoClasse ?? "").trim().toUpperCase() === CONSELHO_DO_RECEITUARIO_ESPECIAL;

const ordemDoMotivo = (reason: {
  codMotivoDocumento?: number;
  descricao: string;
}): number => {
  if (isOtherReason(reason.descricao)) return 3;
  if (reason.codMotivoDocumento === COD_MOTIVO_RECEITUARIO_AMARELO) return 2;
  if (reason.codMotivoDocumento === COD_MOTIVO_RECEITUARIO_AZUL) return 1;

  return 0;
};

export const visibleReasons = <
  T extends { codMotivoDocumento?: number; descricao: string; possuiModelo: boolean },
>(
  reasons: T[],
  onlyWithTemplate: boolean,
  emiteReceituarioEspecial: boolean,
): T[] =>
  sortReasons(
    reasons.filter(
      (reason) =>
        (!onlyWithTemplate || reason.possuiModelo) &&
        (emiteReceituarioEspecial || !isReceituarioEspecial(reason.codMotivoDocumento)),
    ),
  );

export const sortReasons = <T extends { codMotivoDocumento?: number; descricao: string }>(
  reasons: T[],
): T[] =>
  [...reasons].sort((first, second) => ordemDoMotivo(first) - ordemDoMotivo(second));

interface UnidadeDaAgenda {
  codUnidade: number;
  nomeLimpo?: string | null;
  siglaUF?: string | null;
  ativo: boolean;
}

interface DadosDeEstados {
  usuariosAgendas: {
    tbUsuariosAgendasUnidades: { Unidade?: UnidadeDaAgenda | null }[];
  }[];
  tbUFs: { siglaUF: string; nome: string }[];
  tiposDocumentosRegulatorios?:
    | ({ unidades: { codUnidade: number; cadastroCnesCompleto: boolean }[] } | null)[]
    | null;
}

export interface EstadoDaDeclaracao {
  siglaUF: string;
  nome: string;
  unidades: string[];
  unavailable: boolean;
}

export const montaEstados = (dados?: DadosDeEstados | null): EstadoDaDeclaracao[] => {
  if (!dados) return [];

  const elegiveis = new Set(
    (dados.tiposDocumentosRegulatorios?.[0]?.unidades ?? [])
      .filter((unidade) => unidade.cadastroCnesCompleto)
      .map((unidade) => unidade.codUnidade),
  );

  const nomesDeEstado = new Map(dados.tbUFs.map((uf) => [uf.siglaUF, uf.nome]));

  const daAgenda = dados.usuariosAgendas
    .flatMap((agenda) => agenda.tbUsuariosAgendasUnidades)
    .map((vinculo) => vinculo.Unidade)
    .filter((unidade): unidade is UnidadeDaAgenda =>
      Boolean(unidade?.ativo && unidade.siglaUF),
    );

  const porEstado = new Map<string, Map<number, string>>();

  for (const unidade of daAgenda) {
    const sigla = unidade.siglaUF as string;
    const atual = porEstado.get(sigla) ?? new Map<number, string>();

    if (elegiveis.has(unidade.codUnidade)) {
      atual.set(unidade.codUnidade, unidade.nomeLimpo ?? `Unidade ${unidade.codUnidade}`);
    }

    porEstado.set(sigla, atual);
  }

  return [...porEstado.entries()]
    .map(([siglaUF, unidades]) => ({
      siglaUF,
      nome: nomesDeEstado.get(siglaUF) ?? siglaUF,
      unidades: [...unidades.values()].sort((a, b) => a.localeCompare(b, "pt-BR")),
      unavailable: unidades.size === 0,
    }))
    .sort(
      (primeiro, segundo) =>
        Number(primeiro.unavailable) - Number(segundo.unavailable) ||
        primeiro.nome.localeCompare(segundo.nome, "pt-BR"),
    );
};

export const isBindingDeclaration = (codTipoDocumentoRegulatorio: number): boolean =>
  codTipoDocumentoRegulatorio === COD_TIPO_DECLARACAO_VINCULO;

export const visibleDocumentTypes = <T extends { codTipoDocumentoRegulatorio: number }>(
  documentTypes: T[],
  declarationEnabled: boolean,
): T[] =>
  documentTypes
    .filter(
      (documentType) =>
        declarationEnabled ||
        !isBindingDeclaration(documentType.codTipoDocumentoRegulatorio),
    )
    .sort(
      (first, second) =>
        Number(isBindingDeclaration(second.codTipoDocumentoRegulatorio)) -
        Number(isBindingDeclaration(first.codTipoDocumentoRegulatorio)),
    );

export interface UnidadeParaAssinatura {
  codUnidade: number;
  nomeLimpo?: string | null;
  fotoUrl?: string | null;
  siglaUF?: string | null;
  ativo: boolean;
  logradouro?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  Cidade?: { nome: string } | null;
}

interface DadosDeUnidadesParaAssinatura {
  usuariosAgendas: {
    tbUsuariosAgendasUnidades: { Unidade?: UnidadeParaAssinatura | null }[];
  }[];
  tiposDocumentosRegulatorios?:
    | ({
        unidades: { codUnidade: number; cadastroReceituarioCompleto: boolean }[];
      } | null)[]
    | null;
}

export const montaEnderecoDaUnidade = (unidade: UnidadeParaAssinatura): string =>
  [unidade.logradouro, unidade.complemento, unidade.bairro, unidade.Cidade?.nome]
    .filter((parte) => Boolean(parte?.trim()))
    .join(" - ");

export const unidadesDoEstado = (
  dados: DadosDeUnidadesParaAssinatura | undefined,
  siglaUF: string,
): { unidade: UnidadeParaAssinatura; apta: boolean }[] => {
  if (!dados) return [];

  const aptas = new Set(
    (dados.tiposDocumentosRegulatorios?.[0]?.unidades ?? [])
      .filter((unidade) => unidade.cadastroReceituarioCompleto)
      .map((unidade) => unidade.codUnidade),
  );

  const daAgenda = dados.usuariosAgendas
    .flatMap((agenda) => agenda.tbUsuariosAgendasUnidades)
    .map((vinculo) => vinculo.Unidade)
    .filter(
      (unidade): unidade is UnidadeParaAssinatura =>
        Boolean(unidade?.ativo) && unidade?.siglaUF === siglaUF,
    );

  const porUnidade = new Map<number, UnidadeParaAssinatura>();
  for (const unidade of daAgenda) {
    porUnidade.set(unidade.codUnidade, unidade);
  }

  return [...porUnidade.values()]
    .map((unidade) => ({ unidade, apta: aptas.has(unidade.codUnidade) }))
    .sort(
      (primeiro, segundo) =>
        Number(!primeiro.apta) - Number(!segundo.apta) ||
        (primeiro.unidade.nomeLimpo ?? "").localeCompare(
          segundo.unidade.nomeLimpo ?? "",
          "pt-BR",
        ),
    );
};
